import {
	MouseEvent,
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	markAsOrdered,
	OrderedReadonlyArray,
	useActivityFeedStore
} from "../store";
import {
	Anchor,
	BusyIndicator,
	Button,
	ConfirmationDialog,
	FormattedText,
	NoResults,
	ScrollableVirtualList,
	ScrollableVirtualListScrollEventArgs,
	ScrollableVirtualListHandle
} from "client/Components";
import {localize} from "client_legacy/ndaw/culture";
import {
	AvailableNotificationViewModel,
	MutedActivityFilterKind,
	MutedActivityFilterModel,
	NotificationKind,
	VolatileActivitiesClientModel
} from "client/Api/Notifications/notifications";
import { ActivitiesContainer } from "../Container/ActivitiesContainer";
import {
	ActivityStackState,
	groupActivities,
	IActivityStack
} from "../stacks";
import {
	GeneralFeedItemModel,
	flattenActivityStacks,
	isStack
} from "../flattened";
import { ActivityFeedItem } from "../ActivityFeedItem";
import {
	muteActivityKind,
	muteAnnouncementReactions,
	muteCommunityReactions,
	muteCustomActivityTag,
	muteCustomActivityWithoutTag,
	muteEventReactions,
	muteKnowledgeItemReactions,
	muteNewApps,
	showNotificationSettingsDialog
} from "../Settings/NotificationSettingsDialog";
import {
	useForceRerender,
	AlertManager,
	useAlerts
} from "client/Hooks";
import { setTrackingId } from "client/Misc/utils";
import { VolatileActivities } from "../Volatile/VolatileActivities";
import styles from "./ActivityList.module.less";

type RenderFromState = {
	shouldRenderNewActivities: true;
} | {
	shouldRenderNewActivities: false;
	firstActivityId: number;
};

export const ActivityList = (): ReactElement => {
	const [stackStates, setStackStates] = useState<Map<string, ActivityStackState>>(new Map());
	const [stackForDeletionConfirmation, setStackForDeletionConfirmation] = useState<IActivityStack | null>(null);
	const [shouldShowDeleteAllConfirmationDialog, setShouldShowDeleteAllConfirmationDialog] = useState<boolean>(false);
	const [isDeletingAll, setIsDeletingAll] = useState<boolean>(false);

	const EXPECTED_HEIGHT_OF_NOTIFICATION = 100;
	const activities: OrderedReadonlyArray<AvailableNotificationViewModel> = useActivityFeedStore(state => state.activities);
	const muteFilters: readonly MutedActivityFilterModel[] = useActivityFeedStore(s => s.muteFilters);
	const loadUpdates: () => void = useActivityFeedStore(state => state.loadUpdates);
	const isAppointmentMuted: boolean = muteFilters.some(f => f.kind === MutedActivityFilterKind.Appointment);

	const renderFromRef: RefObject<RenderFromState> = useRef<RenderFromState>({ shouldRenderNewActivities: true });
	const renderFrom: Readonly<RenderFromState> = renderFromRef.current;
	const virtualListRef: RefObject<ScrollableVirtualListHandle<GeneralFeedItemModel> | null> = useRef<ScrollableVirtualListHandle<GeneralFeedItemModel>>(null);

	const [rerender] = useForceRerender();
	const isLoading: boolean = useActivityFeedStore(state => state.isLoading);
	const deleteNotifications: (ids: readonly number[]) => Promise<void> = useActivityFeedStore(x => x.delete);
	const deleteAll: () => Promise<void> = useActivityFeedStore(x => x.deleteAll);
	const loadFilters: () => Promise<void> = useActivityFeedStore(x => x.loadFilters);
	const markAllAsRead: () => void = useActivityFeedStore(x => x.markAllAsRead);
	const volatileActivities: VolatileActivitiesClientModel | null = useActivityFeedStore((s) => s.volatileActivities);
	const startPollingVolatileActivities: () => void = useActivityFeedStore(s => s.startPollingVolatileActivities);
	const stopPollingVolatileActivities: () => void = useActivityFeedStore(s => s.stopPollingVolatileActivities);

	const alertManager: AlertManager = useAlerts();

	useEffect(markAllAsRead, [markAllAsRead]);

	const amountOfDeleteableActivities: number = activities.filter(a => a.canBeDeletedByUser).length;
	let indexOfFirstActivityToRender: number = 0;

	if (renderFrom.shouldRenderNewActivities === false) {
		indexOfFirstActivityToRender = activities.findIndex(a => a.id === renderFrom.firstActivityId);
		if (indexOfFirstActivityToRender === -1) {
			indexOfFirstActivityToRender = 0;
		}
	}

	const activitiesToRender: OrderedReadonlyArray<AvailableNotificationViewModel> = useMemo(
		() => {
			return markAsOrdered({
				alreadyOrderedItems: activities.slice(
					indexOfFirstActivityToRender,
					activities.length
				)
			});
		},
		[
			activities,
			indexOfFirstActivityToRender
		]
	);

	const grouppedActivities = useMemo(
		() => groupActivities(activitiesToRender),
		[activitiesToRender]
	);

	const flattenedItems: ReadonlyArray<GeneralFeedItemModel> = useMemo(
		() => flattenActivityStacks(grouppedActivities, stackStates),
		[grouppedActivities, stackStates]
	);
	const isBusy: boolean = isLoading || isDeletingAll;

	const scrollToTopHandler = (): void => {
		if (virtualListRef.current) {
			renderFromRef.current = {
				shouldRenderNewActivities: true
			};
			virtualListRef.current.scrollToTop();
			rerender();
		}
	};

	const scrollToItemHandler = (item: GeneralFeedItemModel): void => {
		if (virtualListRef.current) {
			virtualListRef.current
				.scrollToItem(item);
		}
	};

	const stackStateChangedHandler = useCallback(
		(stackId: string, state: ActivityStackState): void => {
			setStackStates(stackStates => {
				if (stackStates.get(stackId) === state) {
					return stackStates;
				}

				const result = new Map(stackStates);
				result.set(stackId, state);

				return result;
			});
		},
		[]
	);

	const clearAllHandler = (event: MouseEvent): void => {
		event.preventDefault();
		setShouldShowDeleteAllConfirmationDialog(true);
	};

	const confirmClearAllHandler = useCallback(
		async (): Promise<void> => {
			setShouldShowDeleteAllConfirmationDialog(false);

			try {
				setIsDeletingAll(true);
				await deleteAll();
			} catch (e) {
				alertManager.showError(localize("_FailedToDeleteAllNotifications"));
				console.error(e);
			} finally {
				setIsDeletingAll(false);
			}
		},
		[alertManager, deleteAll]
	);

	const showActivityMutedAlert = useCallback(
		(): void => {
			alertManager.showInfo(
				<FormattedText
					text={localize("_ActivityMutedInfoMessage")}
					thingsToReplace={[
						{
							what: "{{ActivityFeedSettings}}",
							filler: (
								<Anchor
									onClick={showNotificationSettingsDialog}
								>
									{localize("_NotificationSettings")}
								</Anchor>
							)
						}
					]}
				/>
			);
		},
		[alertManager]
	);

	const deleteActivityHandler = useCallback(
		async (activity: AvailableNotificationViewModel): Promise<void> => {
			try {
				await deleteNotifications([activity.id]);
			} catch (e) {
				console.error(e);
				alertManager.showError(localize("_FailedToDeleteNotification"));
			}
		},
		[alertManager, deleteNotifications]
	);

	const muteActivityHandler = useCallback(
		async (activity: AvailableNotificationViewModel): Promise<void> => {
			try {
				switch (activity.kind) {
					case NotificationKind.Custom:
						if (activity.data.tag) {
							await muteCustomActivityTag(activity.data.tag);
						} else {
							await muteCustomActivityWithoutTag();
						}
						break;
					case NotificationKind.NewMainAppPromotion:
					case NotificationKind.AppAccess:
						await muteNewApps();
						break;
					case NotificationKind.AnnouncementCommentReaction:
					case NotificationKind.AnnouncementReaction:
						await muteAnnouncementReactions();
						break;
					case NotificationKind.CommunityPostReaction:
					case NotificationKind.CommunityPostCommentReaction:
						await muteCommunityReactions();
						break;
					case NotificationKind.EventReaction:
					case NotificationKind.EventCommentReaction:
						await muteEventReactions();
						break;
					case NotificationKind.KnowledgeItemReaction:
					case NotificationKind.KnowledgeItemCommentReaction:
						await muteKnowledgeItemReactions();
						break;
					case NotificationKind.Unknown:
						throw new Error("Cannot mute activities of Unknown kind");
					default:
						await muteActivityKind(activity.kind);
						break;
				}
				await loadFilters();
				showActivityMutedAlert();
			} catch (e) {
				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}
		},
		[alertManager, loadFilters, showActivityMutedAlert]
	);

	const deleteStackHandler = useCallback(
		(stackId: string): void => {
			const stack: IActivityStack = grouppedActivities.find(s => isStack(s) && s.id === stackId) as IActivityStack;
			setStackForDeletionConfirmation(stack);
		},
		[grouppedActivities]
	);

	const deleteStackConfirmationHandler = useCallback(
		async (): Promise<void> => {
			try {

				if (!stackForDeletionConfirmation) {
					throw new Error("Confirmation does not have any data associated with it");
				}

				const deleteableActivities: ReadonlyArray<AvailableNotificationViewModel> = stackForDeletionConfirmation.activities
					.filter(a => a.canBeDeletedByUser);

				await deleteNotifications(deleteableActivities.map(a => a.id));

				setStackForDeletionConfirmation(null);
			} catch (e) {
				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}
		},
		[alertManager, deleteNotifications, stackForDeletionConfirmation]
	);

	const muteStackHandler = useCallback(
		async (stackId: string): Promise<void> => {
			const stack: IActivityStack = grouppedActivities.find(s => isStack(s) && s.id === stackId) as IActivityStack;
			await stack.muteStack();
			await loadFilters();
			showActivityMutedAlert();
		},
		[grouppedActivities, loadFilters, showActivityMutedAlert]
	);

	const scrollHandler = (args: ScrollableVirtualListScrollEventArgs): void => {
		const {
			scrollPosition
		} = args;

		if (scrollPosition > EXPECTED_HEIGHT_OF_NOTIFICATION && activities.length > 0) {

			if (renderFromRef.current.shouldRenderNewActivities === true) {
				renderFromRef.current = {
					shouldRenderNewActivities: false,
					firstActivityId: activities[0].id
				};
			}
		} else {
			if (renderFromRef.current.shouldRenderNewActivities === false) {
				renderFromRef.current = {
					shouldRenderNewActivities: true
				};
			}
		}
	};

	const isSeparatorVisible: boolean = Boolean(volatileActivities?.appointment) &&
		!isAppointmentMuted;

	useEffect(
		() => {
			if (!isAppointmentMuted) {
				startPollingVolatileActivities();
			}

			return () => {
				if (!isAppointmentMuted) {
					stopPollingVolatileActivities();
				}
			};
		},
		[
			isAppointmentMuted,
			startPollingVolatileActivities,
			stopPollingVolatileActivities
		]
	);

	const getIsPreRenderedItem = useCallback(
		(item: GeneralFeedItemModel): boolean => item.isPrerender,
		[]
	);

	const renderBeforeItems = (
		<>
			<VolatileActivities />
			{isSeparatorVisible && (
				<ActivitiesContainer>
					<hr className={styles.divider} />
				</ActivitiesContainer>
			)}
			{amountOfDeleteableActivities > 0 && (
				<ActivitiesContainer>
					<Anchor
						className={styles.clearAll}
						onClick={clearAllHandler}
						trackingId="clear-all"
					>
						{localize("_ClearAll")}
					</Anchor>
					{shouldShowDeleteAllConfirmationDialog && (
						<ConfirmationDialog
							trackingId="clear-all-activities-confirmation-dialog"
							title={localize(
								"_ClearAllActivityConfirmation"
							)}
							confirmText={localize("_Clear")}
							onConfirmation={confirmClearAllHandler}
							onCancellation={() =>
								setShouldShowDeleteAllConfirmationDialog(
									false
								)
							}
						/>
					)}
				</ActivitiesContainer>
			)}
		</>
	);

	return (
		<div
			{...setTrackingId("activity-list")}
			className={styles.root}
		>
			{isBusy && <BusyIndicator />}
			{!isLoading && (
				<div className={styles.wrapper}>
					<ScrollableVirtualList
						estimatedHeightOfAnItem={EXPECTED_HEIGHT_OF_NOTIFICATION}
						preserveScrollBarSpace
						onScroll={scrollHandler}
						ref={virtualListRef}
						items={flattenedItems}
						itemKey="key"
						getIsPrerender={getIsPreRenderedItem}
						renderBeforeItems={renderBeforeItems}
						trackingId="activity-list"
					>
						{(item) => (
							<ActivitiesContainer>
								<ActivityFeedItem
									activityFeedItem={item}
									onDeleteActivity={deleteActivityHandler}
									onDeleteStack={deleteStackHandler}
									onScrollToItem={scrollToItemHandler}
									onStackStateChanged={stackStateChangedHandler}
									onMuteActivity={muteActivityHandler}
									onMuteStack={muteStackHandler}
									onActivityChanged={loadUpdates}
								/>
							</ActivitiesContainer>
						)}
					</ScrollableVirtualList>
				</div>
			)}
			{!isLoading && flattenedItems.length === 0 && (
				<div className={styles.noResults}>
					<NoResults
						trackingId="no-activities"
						icon="bell"
						title={localize("_NoActivities")}
						message={localize("_ThereAreNoActivitiesAvailable")}
						height="fill"
					/>
				</div>
			)}
			<div className={styles.actionBar}>
				{indexOfFirstActivityToRender > 0 && (
					<div className={styles.actionBarShadow}>
						<Button
							kind="primary"
							text={`${localize(
								"_NewActivities"
							)} (${indexOfFirstActivityToRender})`}
							onClick={scrollToTopHandler}
							width="fill"
						/>
					</div>
				)}
			</div>

			{stackForDeletionConfirmation && (
				<ConfirmationDialog
					title={
						<FormattedText
							text={localize(
								"_AreYouSureYouWantToDeleteAllActivitiesFromCategory"
							)}
							thingsToReplace={[
								{
									what: "{{CategoryTitle}}",
									filler: stackForDeletionConfirmation.title,
								},
							]}
						/>
					}
					confirmText={localize("_Delete")}
					onCancellation={() => setStackForDeletionConfirmation(null)}
					onConfirmation={() => deleteStackConfirmationHandler()}
					trackingId="delete-activities-from-category-confirmation-dialog"
				/>
			)}
		</div>
	);
};

ActivityList.displayName = "ActivityList";
